import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { usePageMeta } from "../hooks/usePageMeta";

export default function PrivacyPage() {
  usePageMeta("privacy");
  
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-20 md:pt-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase mb-8">
            Privacy Policy
          </h1>
          
          <div className="prose prose-zinc max-w-none font-body">
            <p className="text-zinc-600 mb-6">
              Last updated: September 2026
            </p>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">1. Introduction</h2>
              <p>
                Blackline Estimating ("we", "us", "our") operates the Blackline Estimating website and Tender Red Review service. This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">2. Information We Collect</h2>
              <h3 className="font-heading font-bold text-lg mb-2">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Contact information (name, email, phone number, company name)</li>
                <li>Project details and tender information</li>
                <li>Documents you upload (tender documents, estimates, quotations)</li>
                <li>Communications with us</li>
              </ul>
              
              <h3 className="font-heading font-bold text-lg mb-2">2.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6">
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>IP address and approximate location</li>
                <li>Pages visited and time spent</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">3. How We Use Your Information</h2>
              <p>We use your information to:</p>
              <ul className="list-disc pl-6">
                <li>Provide tender review and estimating services</li>
                <li>Communicate with you about your submissions</li>
                <li>Process payments and manage accounts</li>
                <li>Improve our services</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">4. Third-Party Service Providers and AI Processing</h2>
              <p>
                To deliver our services, we may use controlled third-party service providers for specific operational functions, including:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Cloud hosting and secure file storage</li>
                <li>Email delivery services</li>
                <li>Payment processing</li>
                <li>AI-assisted analysis tools to support review efficiency</li>
              </ul>
              <p>
                These service providers operate under contractual obligations to protect your data and use it only for the specific services we engage them to perform. They do not have independent access to use your data for their own purposes.
              </p>
              <p className="mt-4">
                <strong>AI Processing:</strong> We may use AI-assisted tools to support our review processes (such as document parsing, calculation checking, or pattern recognition). Any AI processing is performed under our direction as part of delivering your requested service. AI outputs are reviewed by our team before being included in deliverables.
              </p>
              <p className="mt-4">
                Blackline does not intentionally submit customer documents for public or general-purpose AI model training. Where supported, AI service-provider account and data-control settings are configured to prevent submitted information from being used for model training. AI-assisted processing remains subject to the applicable provider terms, privacy controls and contractual arrangements.
              </p>
              <p className="mt-4">
                <strong>Overseas Processing:</strong> Some service providers may store or process information outside New Zealand or Australia. Where overseas processing occurs, Blackline takes reasonable steps to use reputable providers with appropriate privacy, confidentiality and security protections.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">5. Data Security</h2>
              <p>
                We implement appropriate technical and organisational measures to protect your personal information and uploaded documents. All files are stored securely and are not publicly accessible. Access to customer data is restricted to authorised personnel only.
              </p>
              <p className="mt-4">
                If Blackline becomes aware of a privacy or security incident affecting customer information, we will assess the incident and provide notifications where required by applicable law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">6. Data Retention</h2>
              <p>
                <strong>Operational tender files:</strong> We retain tender documents and related files for 90 days after report delivery, unless:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>You request earlier deletion</li>
                <li>Legal, payment, or dispute requirements require longer retention</li>
                <li>Backup copies remain temporarily within normal secure backup cycles</li>
              </ul>
              <p>
                <strong>Contact and account information:</strong> We retain your contact information for as long as necessary to provide our services, manage our business relationship, and comply with legal obligations. You may request deletion of your personal information at any time.
              </p>
              <p className="mt-4">
                To request early deletion of your documents or personal information, contact us at{" "}
                <a href="mailto:info@blacklineestimating.com" className="underline">info@blacklineestimating.com</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">7. Your Rights</h2>
              <p>Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Object to processing</li>
                <li>Data portability</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">8. Privacy Questions and Complaints</h2>
              <p>
                Privacy questions or complaints may be sent to{" "}
                <a href="mailto:info@blacklineestimating.com" className="underline">info@blacklineestimating.com</a>. 
                We will acknowledge and investigate privacy complaints within a reasonable period.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">9. Contact Us</h2>
              <p>
                For privacy-related enquiries, please contact us at:{" "}
                <a href="mailto:info@blacklineestimating.com" className="underline">
                  info@blacklineestimating.com
                </a>
              </p>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-zinc-200">
            <Link to="/" className="font-mono text-sm uppercase tracking-wider hover:underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
